import { groupBy } from "lodash-es";

import { boardTitle } from "@/model/baseTypes";
import type { ResolvedCard, SearchResult } from "@/model/card";
import type { DependencyTeamFilter } from "@/model/search";
import type { Team } from "@/model/session";
import { useArtStore } from "@/store/art";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { NO_TEAM, useTeamStore } from "@/store/team";
import { useGlobalI18n } from "@/translations/i18n";

import type { Grouped } from "./sortSearchResults";
import {
  sortedArtIds,
  sortedBoardIds,
  sortedTeamFilters,
  sortedTeamNames,
  sortedTypeNames,
} from "./sortSearchResults";

const { t, tt } = useGlobalI18n();

type GroupedCards = Grouped<ResolvedCard>;

interface CardsOfArt {
  artId: string;
  artName: string;
  boards: CardsOfBoard[];
}

export interface CardsOfBoard {
  boardId: string;
  boardName: string;
  cards: ResolvedCard[];
}

export function groupResultsByTypeOrTeam(cards: ResolvedCard[]): GroupedCards {
  const groupedByTypeName = groupBy(cards, (item) => item.type.name);
  const typeNameGroups = sortedTypeNames(groupedByTypeName);

  const searchMenuStore = useSearchMenuStore();
  if (!searchMenuStore.isDependencySearch || !searchMenuStore.selectedTeams.length) {
    return Object.fromEntries(typeNameGroups);
  }

  const dependencyStickyTypes = new Set(
    useBoardStore().possibleStickyTypes.flatMap((stickyType) =>
      stickyType.functionality === "dependency" ? stickyType.name : [],
    ),
  );

  const result: GroupedCards = {};
  for (const [typeName, cards] of typeNameGroups) {
    if (dependencyStickyTypes.has(typeName)) {
      for (const [teamName, teamCards] of groupDependencyResultsByTeam(cards)) {
        result[teamName] = [...(result[teamName] ?? []), ...teamCards];
      }
    } else {
      result[typeName] = cards;
    }
  }
  return result;
}

function groupDependencyResultsByTeam(cards: ResolvedCard[]) {
  const filter = useSearchMenuStore().selectedDependencyTeamFilter;
  return groupResultsByTeam(cards.flatMap((item) => applyDependencyTeamFilter(item, filter)));
}

function applyDependencyTeamFilter(item: ResolvedCard, filter: DependencyTeamFilter) {
  const { data: card } = useBoardStore().currentBoard().cards[item.id];
  const isPrecond = useSearchMenuStore().isTeamSelected(card.precondTeam?.id);
  const precond = isPrecond ? [{ ...item, team: card.precondTeam ?? NO_TEAM }] : [];
  const isDepend = useSearchMenuStore().isTeamSelected(card.dependTeam?.id);
  const depend = isDepend ? [{ ...item, team: card.dependTeam ?? NO_TEAM }] : [];

  switch (filter) {
    case "all":
      return [...precond, ...depend];
    case "blocked":
      return precond;
    case "enabling":
      return depend;
    case "mutual":
      return isPrecond && isDepend ? [...precond, ...depend] : [];
  }
}

function groupResultsByTeam(cards: ResolvedCard[]) {
  const groupedByTeamName = groupBy(cards, (item) => item.team?.name || t("general.noTeam"));
  return sortedTeamNames(groupedByTeamName);
}

export function groupResultsByDependencyTeamFilter(cards: ResolvedCard[]): GroupedCards {
  const groupedByTeamFilter = groupBy(cards, (item) => {
    const card = useBoardStore().currentBoard().cards[item.id];
    if (!card || item.team?.id === undefined) {
      return /*$t*/ "depTeamFilter.other";
    }
    if (item.team.id === (card.data.precondTeam?.id || NO_TEAM.id)) {
      return /*$t*/ "depTeamFilter.blocked";
    }
    if (item.team.id === (card.data.dependTeam?.id || NO_TEAM.id)) {
      return /*$t*/ "depTeamFilter.enabling";
    }
    return /*$t*/ "depTeamFilter.other";
  });
  return Object.fromEntries(sortedTeamFilters(groupedByTeamFilter));
}

export function showDependencyGroupingHeader(cards: ResolvedCard[]) {
  return (
    cards.length > 0 &&
    (useBoardStore().currentBoard().type !== "program" ||
      useSearchMenuStore().selectedTeams.length > 0)
  );
}

export function groupResultsByArt(results: SearchResult[]): CardsOfArt[] {
  const groupedByArtId = groupBy(results, (result) => result.artId);
  return sortedArtIds(groupedByArtId).map(([artId, items]) => {
    const art = useArtStore().artById(artId);
    const artName = art?.name || "";
    return { artId, artName, boards: groupResultsByBoard(items) };
  });
}

export function groupResultsByBoard(results: SearchResult[]): CardsOfBoard[] {
  const groupedByBoardId = groupBy(results, (result) => result.boardId);
  return sortedBoardIds(groupedByBoardId).map(([boardId, items]) => ({
    boardId,
    boardName: tt(boardTitle(useBoardsStore().boardById(boardId))),
    cards: items.map((item) => resolveSearchResult(item)),
  }));
}

function resolveSearchResult(result: SearchResult): ResolvedCard {
  const type = useStickyTypeStore().findStickyType(result.typeId);
  const findTeamById = (teamId: Team["id"] | undefined) =>
    useSearchMenuStore().allTeams.find((team: Team) => team.id === teamId);
  const findAnyTeamById = (teamId: Team["id"] | undefined) =>
    teamId ? useTeamStore().findTeam({ id: teamId, withExternal: true }) : undefined;
  return {
    id: result.id,
    text: result.text,
    type,
    flagType: result.flagType,
    iteration: useSessionStore().getIterationById(result.iterationId),
    almId: result.almId,
    points: result.points || 0,
    team: findTeamById(result.teamId),
    precondTeam: findAnyTeamById(result.precondTeamId),
    dependTeam: findAnyTeamById(result.dependTeamId),
    status: result.status,
  };
}
